import { useClient } from '@/composables/useClient.js'
import type { Address, PatchUser, PaymentMethod, PutDefaultAddress, User } from '@repo/api-types'

export const useAccount = () => {
  const getDefaultCustomerAddress = async (): Promise<{
    error?: string | null
    result?: Address
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.getDefaultCustomerAddress()
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const getDefaultPaymentMethod = async (): Promise<{
    error?: string | null
    result?: PaymentMethod
  }> => {
    const client = await useClient()
    const { status, body } = await client.payment.getDefaultPaymentMethod()
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const getUser = async (): Promise<{
    error?: string | null
    result?: User
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.getMe()
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const updateUser = async (
    user: PatchUser,
  ): Promise<{
    error?: string | null
    result?: User
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.patchMe({
      body: user,
    })
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const updatePassword = async (
    newPassword: string,
  ): Promise<{
    error?: string | null
    result?: boolean
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.updatePassword({
      body: {
        newPassword,
      },
    })
    if (status === 200) {
      return {
        result: body.success,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const updateDefaultPaymentMethod = async (
    paymentMethodId: string,
    provider: string,
  ): Promise<{
    error?: string | null
    result?: PaymentMethod
  }> => {
    const client = await useClient()
    const { status, body } = await client.payment.updateDefaultPaymentMethod({
      body: {
        paymentMethodId,
        provider,
      },
    })
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const updateDefaultCustomerAddress = async (
    address: PutDefaultAddress,
  ): Promise<{
    error?: string | null
    result?: boolean
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.updateDefaultCustomerAddress({
      body: address,
    })
    if (status === 200) {
      return {
        result: body.success,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  const checkUserEmailExists = async (
    email: string,
  ): Promise<{
    error?: string | null
    result?: { isTaken: boolean }
  }> => {
    const client = await useClient()
    const { status, body } = await client.auth.isEmailTaken({
      query: {
        email,
      },
    })
    if (status === 200) {
      return {
        result: body,
      }
    }
    return { error: (body as unknown as { error?: string }).error || 'Unspecified server error' }
  }

  return {
    getDefaultCustomerAddress,
    getDefaultPaymentMethod,
    getUser,
    updateUser,
    updatePassword,
    updateDefaultPaymentMethod,
    updateDefaultCustomerAddress,
    checkUserEmailExists,
  }
}
